import {useEffect, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useBgDerivation} from "hooks/useBgDerivation";
import {
  buildBgCatalogIndex,
  type BgCatalogIndex,
  type CatalogBackgroundLike,
} from "libs/bgCatalogIndex";

/**
 * Publish/read the background display-image index (issue #567, B3).
 *
 * App owns the catalog fetch (it knows the channel gate and the current
 * product), but the consumers of the resolved urls are scattered — useBgUrl runs
 * inside per-product child Setting providers, BgSelector sits in the Toolbar,
 * the marker classNames are applied in App itself. Rather than thread the list
 * through every one of them, App publishes the reshaped index into a
 * product-agnostic React Query entry and consumers subscribe to it, exactly the
 * pattern the legacy tile cache uses (`["get-backgrounds", channel, type]`,
 * #251). React Query stays the only cache — nothing is mirrored into context.
 */

/**
 * Cache key for the published index. Product-agnostic on purpose: the ALL view's
 * child providers each read their own product out of one shared entry. `type` is
 * normalized the same way as the tile cache's key so both agree for callers that
 * haven't got a type yet.
 */
export const getBgCatalogIndexQueryKey = (channel: string | undefined, type: string | undefined) =>
  ["bg-catalog-index", channel, type ?? "general"] as const;

/**
 * Read side. Returns null unless the derivation path applies to THIS route and
 * App has published for this (channel, type) — which is what keeps every
 * consumer on its legacy filename-matching path when the flag is off.
 *
 * The route gate is not redundant with the publish gate: `setQueryData` writes
 * an entry that outlives the route that produced it. A warm renderer page loads
 * `/` (derivation on, publishes social/general for the root's product), then SPA-
 * navigates to `/scene/:id`, where derivation is deliberately off — and read the
 * leftover entry, whose product doesn't match the scene's. `lookupBgImage` misses,
 * `deriveBgUrl` treats the catalog as authoritative and resolves to "", and
 * BgTransformer sits on its <Loader/> forever: every server export came out with
 * no background at all (issue #567 — 99 KB blank vs 560 KB real, flag-on only).
 */
export function useBgCatalogIndex(
  channel: string | undefined,
  type: string | undefined
): BgCatalogIndex | null {
  const bgDerivation = useBgDerivation();
  const {data} = useQuery({
    queryKey: getBgCatalogIndexQueryKey(channel, type),
    // Never fetched — this entry is written by usePublishBgCatalogIndex. Throw
    // rather than return a benign null so an accidental `enabled: true` surfaces
    // instead of silently degrading every consumer to "no index".
    queryFn: () => {
      throw new Error("bg-catalog-index is published by App, never fetched");
    },
    enabled: false,
  });
  return bgDerivation ? (data ?? null) : null;
}

/**
 * The published index read WITHOUT the derivation gate, for the one consumer
 * that treats it as a fallback rather than as the authority: `deriveBgUrl`
 * consults it only after the legacy url array has nothing for the product
 * (issue #698 — catalog-only backgrounds on the saved-scene path).
 *
 * The stale-entry hazard described above does not apply here. The entry is
 * keyed by product, so a leftover index from another route either misses (and
 * the result is the legacy "" it would have been anyway) or names the very
 * image the catalog holds for this product on this channel and type.
 */
export function useBgCatalogFallbackIndex(
  channel: string | undefined,
  type: string | undefined
): BgCatalogIndex | null {
  const {data} = useQuery({
    queryKey: getBgCatalogIndexQueryKey(channel, type),
    queryFn: () => {
      throw new Error("bg-catalog-index is published by App, never fetched");
    },
    enabled: false,
  });
  return data ?? null;
}

/**
 * Write side. Builds the index from the catalog list and publishes it for the
 * current (channel, type). Inert (and publishes nothing) when `enabled` is
 * false, so the flag-off path never sees an entry. Returns the index for callers
 * that already have it in scope.
 */
export function usePublishBgCatalogIndex({
  channel,
  type,
  product,
  backgrounds,
  enabled,
}: {
  channel: string | undefined;
  type: string | undefined;
  product: string | undefined;
  backgrounds: CatalogBackgroundLike[] | null | undefined;
  enabled: boolean;
}): BgCatalogIndex | null {
  const queryClient = useQueryClient();
  const index = useMemo(
    () => (enabled ? buildBgCatalogIndex(backgrounds, product) : null),
    [enabled, backgrounds, product]
  );
  const queryKey = useMemo(() => getBgCatalogIndexQueryKey(channel, type), [channel, type]);

  useEffect(() => {
    if (!index) return;
    queryClient.setQueryData(queryKey, index);
  }, [index, queryKey, queryClient]);

  return index;
}
